import { LAppDelegate } from './lappdelegate';
import * as LAppDefine from './lappdefine';

type VoiceLanguage = 'cn' | 'en';
type ViewInteractionMode = 'interact' | 'pan';

type RecordingType = {
  mimeType: string;
  extension: string;
  label: string;
};

type RecordingMode = 'manual' | 'motion';
type RenderScaleWindow = Window & { __ronLive2DExportPixelScale?: number };
type OfflineExportFrame = { dataUrl: string; width: number; height: number };
type OfflineExportModelSize = {
  width: number;
  height: number;
  pixelsPerUnit?: number;
  pixelWidth?: number;
  pixelHeight?: number;
};
type OfflineExportDrawableBounds = {
  minX: number;
  maxX: number;
  minY: number;
  maxY: number;
  width: number;
  height: number;
  centerX: number;
  centerY: number;
  drawableCount: number;
  vertexCount: number;
  coordinateSpace: 'model' | 'model_matrix';
};
type LocalizedVoice = {
  sound?: string;
  sounds?: string[];
  subtitle?: string;
};

type MotionDetail = {
  showId?: number;
  audioId?: number;
  desc?: string;
  group: string;
  index: number;
  actionId?: string;
  label: string;
  duration: number;
  voices?: Partial<Record<VoiceLanguage, LocalizedVoice>>;
};

type HideGroupConfig = {
  id: string;
  label: string;
  partIds?: string[];
  drawableIds?: string[];
  matchPrefixes?: string[];
  confidence?: string;
  candidateCount?: number;
  semanticCandidateCount?: number;
};

type MotionButtonConfig = {
  group: string;
  index: number;
  actionId: string;
  label: string;
  duration: number;
};

type StoryCue = {
  step: number;
  action: string;
  speaker: string;
  subtitle: string;
  voiceId: string;
  audioName: string;
  motion?: MotionButtonConfig;
  voices: Partial<Record<VoiceLanguage, LocalizedVoice>>;
};

type StorySegment = {
  id: string;
  story: string;
  label: string;
  startStep: number;
  endStep: number;
  cues: StoryCue[];
};

type StoryTouchAction = {
  storyGameId: string;
  story: string;
  storyStep: number;
  hitArea: string;
  sourceHitAreas: string[];
  subtitles: string[];
  audioId: string;
  audioName: string;
  eventSource: string;
  defaultSet: boolean;
  motion?: MotionButtonConfig;
  voices: Partial<Record<VoiceLanguage, LocalizedVoice>>;
};

type ModelConfig = {
  id: string;
  title: string;
  assetKind?: string;
  categoryLabel?: string;
  modelDir?: string;
  sourceModelDir?: string;
  variantKey?: string;
  variantLabel?: string;
  variantSourceLayer?: string;
  styleId?: string;
  styleName?: string;
  stage?: string;
  hideGroups?: HideGroupConfig[];
  ambientMotion?: MotionButtonConfig;
  storySegments?: StorySegment[];
  storyTouchActions?: StoryTouchAction[];
  unlockableMotions: MotionButtonConfig[];
  motionGroups: Array<{
    label: string;
    actions: MotionButtonConfig[];
  }>;
};

type ModelConfigEvent = {
  sceneIndex: number;
  models: Array<{ id: string; title: string; assetKind?: string; categoryLabel?: string; modelDir?: string; styleId?: string; styleName?: string; stage?: string; variantKey?: string; variantLabel?: string; variantSourceLayer?: string }>;
  config: ModelConfig;
};

const unlockedMotionStorageKeyPrefix = 'live2d-unlocked-motions:';
const accessPasswordMetaName = 'ron-access-password-sha256';
const accessSessionStorageKeyPrefix = 'ron-live2d-access:';
let live2DModelReady = false;
let viewerBooted = false;

window.addEventListener(
  'load',
  (): void => {
    void startViewerAfterAccessCheck();
  },
  { passive: true }
);

window.addEventListener(
  'beforeunload',
  (): void => {
    if (viewerBooted) {
      LAppDelegate.releaseInstance();
    }
  },
  { passive: true }
);

function bootViewer(): void {
  if (viewerBooted) {
    return;
  }
  if (!LAppDelegate.getInstance().initialize()) {
    return;
  }

  viewerBooted = true;
  setupMotionPanel();
  setupModelVisibility();

  if (isOfflineExportMode()) {
    startOfflineWarmupLoop();
  } else {
    LAppDelegate.getInstance().run();
  }
}

function readAccessPasswordHash(): string {
  const value = document.querySelector<HTMLMetaElement>(`meta[name="${accessPasswordMetaName}"]`)?.content.trim().toLowerCase() || '';
  return /^[a-f0-9]{64}$/.test(value) ? value : '';
}

function isAccessSessionUnlocked(storageKey: string): boolean {
  try {
    return window.sessionStorage.getItem(storageKey) === 'unlocked';
  } catch {
    return false;
  }
}

function rememberAccessSession(storageKey: string): void {
  try {
    window.sessionStorage.setItem(storageKey, 'unlocked');
  } catch {
    // Access still succeeds when session storage is unavailable.
  }
}

async function sha256Hex(value: string): Promise<string> {
  if (!window.crypto?.subtle) {
    throw new Error('当前浏览器环境不支持安全密码校验，请通过 HTTPS 或本机地址打开。');
  }
  const digest = await window.crypto.subtle.digest('SHA-256', new TextEncoder().encode(value));
  return Array.from(new Uint8Array(digest), byte => (byte + 0x100).toString(16).slice(-2)).join('');
}

async function leaveAccessGate(gate: HTMLElement): Promise<void> {
  gate.classList.add('is-leaving');
  await new Promise<void>(resolve => window.setTimeout(resolve, 180));
  gate.hidden = true;
}

async function startViewerAfterAccessCheck(): Promise<void> {
  const expectedHash = readAccessPasswordHash();
  if (!expectedHash) {
    bootViewer();
    return;
  }

  const gate = document.querySelector<HTMLElement>('#access-gate');
  const form = document.querySelector<HTMLFormElement>('#access-form');
  const input = document.querySelector<HTMLInputElement>('#access-password');
  const submit = document.querySelector<HTMLButtonElement>('#access-submit');
  const error = document.querySelector<HTMLElement>('#access-error');
  if (!gate || !form || !input || !submit || !error) {
    return;
  }

  const storageKey = `${accessSessionStorageKeyPrefix}${expectedHash}`;
  if (isAccessSessionUnlocked(storageKey)) {
    bootViewer();
    return;
  }

  gate.hidden = false;
  window.requestAnimationFrame(() => input.focus());
  form.addEventListener('submit', event => {
    event.preventDefault();
    submit.disabled = true;
    input.disabled = true;
    input.setAttribute('aria-invalid', 'false');
    error.textContent = '';

    void sha256Hex(input.value)
      .then(async actualHash => {
        if (actualHash !== expectedHash) {
          input.value = '';
          input.disabled = false;
          submit.disabled = false;
          input.setAttribute('aria-invalid', 'true');
          error.textContent = '密码不正确，请重新输入。';
          input.focus();
          return;
        }

        rememberAccessSession(storageKey);
        await leaveAccessGate(gate);
        bootViewer();
      })
      .catch(reason => {
        input.disabled = false;
        submit.disabled = false;
        input.setAttribute('aria-invalid', 'true');
        error.textContent = reason instanceof Error ? reason.message : '密码校验失败，请重试。';
        input.focus();
      });
  });
}

function startOfflineWarmupLoop(): void {
  const tick = (): void => {
    LAppDelegate.getInstance().renderFrame();
    if (!live2DModelReady) {
      window.requestAnimationFrame(tick);
    }
  };
  tick();
}

function setupModelVisibility(): void {
  const showModel = (ready = false): void => {
    if (ready) {
      live2DModelReady = true;
    }
    document.body.classList.add('live2d-ready');
  };

  window.addEventListener('live2d-model-ready', () => showModel(true), { once: true });
  window.setTimeout(() => showModel(false), 5000);
}

function setupMotionPanel(): void {
  const modelSelect = document.querySelector<HTMLSelectElement>('#model-select');
  const stageTabs = document.querySelector<HTMLElement>('#stage-tabs');
  const panelTitle = document.querySelector<HTMLElement>('#panel-title-text');
  const unlockSection = document.querySelector<HTMLElement>('#unlock-section');
  const unlockRow = document.querySelector<HTMLElement>('#unlock-row');
  const motionGroups = document.querySelector<HTMLElement>('#motion-groups');
  const freeTouchButton = document.querySelector<HTMLButtonElement>(
    '#free-touch-button'
  );
  const languageButtons = Array.from(
    document.querySelectorAll<HTMLButtonElement>('.language-button')
  );
  const statusName = document.querySelector<HTMLElement>(
    '#motion-status-name'
  );
  const statusTime = document.querySelector<HTMLElement>(
    '#motion-status-time'
  );
  const progressFill = document.querySelector<HTMLElement>(
    '#motion-progress-fill'
  );
  const subtitleLayer = document.querySelector<HTMLElement>('#subtitle-layer');
  const subtitleMeta = document.querySelector<HTMLElement>('#subtitle-meta');
  const subtitleText = document.querySelector<HTMLElement>('#subtitle-text');
  const subtitleToggle = document.querySelector<HTMLInputElement>('#subtitle-toggle');
  const protagonistToggle = document.querySelector<HTMLInputElement>('#protagonist-toggle');
  const protagonistToggleWrap = document.querySelector<HTMLElement>('#protagonist-toggle-wrap');
  const panelHideButton = document.querySelector<HTMLButtonElement>('#panel-hide-button');
  const panelRestoreButton = document.querySelector<HTMLButtonElement>('#panel-restore-button');
  const voiceVolumeSlider = document.querySelector<HTMLInputElement>('#voice-volume');
  const voiceVolumeValue = document.querySelector<HTMLElement>('#voice-volume-value');
  const viewZoomValue = document.querySelector<HTMLElement>('#view-zoom-value');
  const viewZoomReset = document.querySelector<HTMLButtonElement>('#view-zoom-reset');
  const viewModeButtons = Array.from(document.querySelectorAll<HTMLButtonElement>('.view-mode-button'));
  const motionLoopToggle = document.querySelector<HTMLInputElement>('#motion-loop-toggle');
  const exportMotionButton = document.querySelector<HTMLButtonElement>('#export-motion-button');
  const offlineExportButton = document.querySelector<HTMLButtonElement>('#offline-export-motion-button');
  const recordButton = document.querySelector<HTMLButtonElement>('#record-button');
  const recordStatus = document.querySelector<HTMLElement>('#record-status');
  const storyPerformance = document.querySelector<HTMLElement>('#story-performance');
  const storyPerformanceCount = document.querySelector<HTMLElement>('#story-performance-count');
  const storySegmentSelect = document.querySelector<HTMLSelectElement>('#story-segment-select');
  const storyPlayButton = document.querySelector<HTMLButtonElement>('#story-play-button');
  const storyStopButton = document.querySelector<HTMLButtonElement>('#story-stop-button');
  const storyCueList = document.querySelector<HTMLElement>('#story-cue-list');

  let progressFrame = 0;
  let progressStart = 0;
  let progressDuration = 0;
  let currentVoice: HTMLAudioElement | null = null;
  let currentLanguage: VoiceLanguage = readLanguage();
  let subtitleVisible = readSubtitleVisible();
  let panelHidden = readPanelHidden();
  let voiceVolume = readVoiceVolume();
  let viewZoom = 1.0;
  let viewInteractionMode: ViewInteractionMode = readViewInteractionMode();
  let spacePanActive = false;
  let currentHideGroups: HideGroupConfig[] = [];
  let hideProtagonist = false;
  let motionLoopEnabled = readMotionLoopEnabled();
  let lastVoiceDetail: MotionDetail | null = null;
  let currentManualDetail: MotionDetail | null = null;
  let currentModelId = '';
  let currentModelConfig: ModelConfig | null = null;
  let storySegments: StorySegment[] = [];
  let selectedStorySegmentId = '';
  let storyPlaybackToken = 0;
  let storySequenceRunning = false;
  let storyCuePlaying = false;
  let activeStoryCue: StoryCue | null = null;
  let activeStorySegmentId = '';
  let activeStoryCueIndex = -1;
  const storyTapNextCueBySegment = new Map<string, number>();
  let lastStoryMotionKey = '';
  let currentVoiceFinish: (() => void) | null = null;
  let mediaRecorder: MediaRecorder | null = null;
  let recordingMode: RecordingMode | null = null;
  let recordingMotionDetail: MotionDetail | null = null;
  let recordingStream: MediaStream | null = null;
  let recordingChunks: Blob[] = [];
  let recordingStartedAt = 0;
  let recordingTimer = 0;
  let motionExportStopTimer = 0;
  let recordingExtension = 'webm';
  let recordingMimeType = 'video/webm';
  let recordingResolution = '';
  let recordingFrameRateValue = 0;
  let motionExportPreparing = false;
  let activeMotionExportPixelScale = 1;
  const localOfflineExportAvailable = isLocalOfflineExportAvailable();
  let modelPickerGroups = new Map<string, { key: string; label: string; items: Array<{ index: number; id: string; title: string; stage?: string; variantKey?: string; variantLabel?: string; variantSourceLayer?: string }> }>();

  const allMotionButtons = (): HTMLButtonElement[] => {
    return Array.from(document.querySelectorAll<HTMLButtonElement>('.motion-button')).filter(
      button => button.dataset.group != null
    );
  };

  const syncLanguageButtons = (): void => {
    languageButtons.forEach(button => {
      button.classList.toggle(
        'active',
        button.dataset.language === currentLanguage
      );
    });
    document.documentElement.lang = currentLanguage === 'cn' ? 'zh-CN' : 'en';
  };

  const syncUnlockButtons = (): void => {
    const unlockedMotions = readUnlockedMotions(currentModelId);
    const unlockButtons = Array.from(
      document.querySelectorAll<HTMLButtonElement>('.unlock-toggle')
    );

    unlockButtons.forEach(button => {
      const value = button.dataset.value;

      if (!value) {
        return;
      }

      button.classList.toggle('active', unlockedMotions.has(value));
      button.setAttribute(
        'aria-pressed',
        unlockedMotions.has(value) ? 'true' : 'false'
      );
    });
  };

  const cancelProgress = (): void => {
    if (progressFrame != 0) {
      cancelAnimationFrame(progressFrame);
      progressFrame = 0;
    }
  };

  const syncSubtitleToggle = (): void => {
    if (subtitleToggle) {
      subtitleToggle.checked = subtitleVisible;
    }
  };

  const syncMotionLoopToggle = (): void => {
    if (motionLoopToggle) {
      motionLoopToggle.checked = motionLoopEnabled;
    }
    LAppDelegate.getInstance().setManualMotionLoop(
      motionLoopEnabled,
      currentManualDetail ? manualPlaybackFromDetail(currentManualDetail) : undefined
    );
  };

  const syncPanelHidden = (): void => {
    document.body.classList.toggle('panel-hidden', panelHidden);
    if (!panelHidden) {
      document.body.classList.remove('panel-peek-hidden');
    }
  };

  const togglePanelHidden = (hidden: boolean): void => {
    panelHidden = hidden;
    localStorage.setItem('live2d-panel-hidden', hidden ? 'yes' : 'no');
    syncPanelHidden();
  };

  const togglePresentationMode = (): void => {
    if (document.body.classList.contains('panel-peek-hidden')) {
      document.body.classList.remove('panel-peek-hidden');
      return;
    }
    panelHidden = true;
    localStorage.setItem('live2d-panel-hidden', 'yes');
    syncPanelHidden();
    document.body.classList.add('panel-peek-hidden');
  };

  const toggleFullscreen = (): void => {
    if (document.fullscreenElement) {
      void document.exitFullscreen().catch((): void => undefined);
      return;
    }
    void document.documentElement.requestFullscreen().catch((): void => undefined);
  };

  const syncVoiceVolume = (): void => {
    const percent = Math.round(voiceVolume * 100);
    if (voiceVolumeSlider) {
      voiceVolumeSlider.value = String(percent);
    }
    if (voiceVolumeValue) {
      voiceVolumeValue.textContent = `${percent}%`;
    }
    if (currentVoice) {
      currentVoice.volume = voiceVolume;
    }
  };

  const clampViewZoom = (value: number): number => {
    if (!Number.isFinite(value)) {
      return 1.0;
    }
    return Math.max(0.6, Math.min(3.0, value));
  };

  const viewZoomStorageKey = (modelId: string): string => `live2d-view-zoom:${modelId || 'default'}`;

  const readViewZoom = (modelId: string): number => {
    const rawValue = Number(localStorage.getItem(viewZoomStorageKey(modelId)) || '100');
    if (!Number.isFinite(rawValue)) {
      return 1.0;
    }
    return clampViewZoom(rawValue / 100);
  };

  const writeViewZoom = (modelId: string, zoom: number): void => {
    localStorage.setItem(viewZoomStorageKey(modelId), String(Math.round(clampViewZoom(zoom) * 100)));
  };

  const dispatchViewZoom = (): void => {
    window.dispatchEvent(new CustomEvent('live2d-view-scale-changed', { detail: { scale: viewZoom } }));
  };

  const syncViewZoom = (): void => {
    viewZoom = clampViewZoom(viewZoom);
    const percent = Math.round(viewZoom * 100);
    if (viewZoomValue) {
      viewZoomValue.textContent = `${percent}%`;
    }
    dispatchViewZoom();
  };

  const isEditableTarget = (target: EventTarget | null): boolean => {
    if (!(target instanceof HTMLElement)) {
      return false;
    }
    if (target.closest?.('#motion-panel')) {
      return true;
    }
    const tagName = target.tagName.toLowerCase();
    return target.isContentEditable || tagName === 'input' || tagName === 'textarea' || tagName === 'select';
  };

  const dispatchViewInteractionMode = (): void => {
    const panEnabled = viewInteractionMode === 'pan' || spacePanActive;
    document.body.classList.toggle('view-pan-enabled', panEnabled);
    window.dispatchEvent(new CustomEvent('live2d-view-pan-mode-changed', {
      detail: { mode: viewInteractionMode, panEnabled, temporary: spacePanActive }
    }));
  };

  const syncViewInteractionMode = (): void => {
    viewModeButtons.forEach(button => {
      const active = button.dataset.viewMode === viewInteractionMode;
      button.classList.toggle('active', active);
      button.setAttribute('aria-pressed', active ? 'true' : 'false');
    });
    dispatchViewInteractionMode();
  };

  const protagonistStorageKey = (modelId: string): string => `live2d-hide-protagonist:${modelId || 'default'}`;

  const readHideProtagonist = (modelId: string): boolean => {
    return localStorage.getItem(protagonistStorageKey(modelId)) === 'yes';
  };

  const writeHideProtagonist = (modelId: string, value: boolean): void => {
    localStorage.setItem(protagonistStorageKey(modelId), value ? 'yes' : 'no');
  };

  const protagonistGroups = (): HideGroupConfig[] => currentHideGroups.filter(group => group.id === 'protagonist');

  const applyProtagonistVisibility = (): void => {
    const groups = hideProtagonist ? protagonistGroups() : [];
    LAppDelegate.getInstance().setHiddenGroups(groups);
  };

  const syncProtagonistToggle = (): void => {
    const groups = protagonistGroups();
    const available = groups.length > 0;
    if (protagonistToggleWrap) {
      protagonistToggleWrap.hidden = !available;
    }
    if (protagonistToggle) {
      protagonistToggle.disabled = !available;
      protagonistToggle.checked = available && hideProtagonist;
      const count = groups[0]?.semanticCandidateCount || groups[0]?.candidateCount || 0;
      protagonistToggle.title = available ? `自动识别 ${count} 个主控图层候选` : '';
    }
    applyProtagonistVisibility();
  };

  const hideSubtitle = (): void => {
    if (!subtitleLayer) {
      return;
    }

    subtitleLayer.classList.remove('visible');
    subtitleLayer.setAttribute('aria-hidden', 'true');

    if (subtitleText) {
      subtitleText.textContent = '';
    }

    if (subtitleMeta) {
      subtitleMeta.textContent = '';
    }
  };

  const showSubtitle = (voice: LocalizedVoice | undefined, metaLabel = '中文字幕'): void => {
    if (!subtitleVisible || !subtitleLayer || !subtitleText || !voice?.subtitle) {
      hideSubtitle();
      return;
    }

    subtitleText.textContent = voice.subtitle;

    if (subtitleMeta) {
      subtitleMeta.textContent = metaLabel;
    }

    subtitleLayer.classList.add('visible');
    subtitleLayer.setAttribute('aria-hidden', 'false');
  };

  const stopVoice = (): void => {
    const finish = currentVoiceFinish;
    currentVoiceFinish = null;
    if (currentVoice) {
      currentVoice.pause();
      currentVoice.currentTime = 0;
      currentVoice = null;
    }

    finish?.();
  };

  const selectVoice = (detail: MotionDetail): LocalizedVoice | undefined => {
    if (detail.voices?.[currentLanguage]) {
      return detail.voices[currentLanguage];
    }

    return detail.voices?.cn;
  };

  const playVoice = (detail: MotionDetail): void => {
    stopVoice();
    lastVoiceDetail = detail;

    const voice = selectVoice(detail);
    showSubtitle(voice);

    if (!voice?.sound) {
      return;
    }

    const modelDir = currentModelId || LAppDefine.ModelDir[0];
    currentVoice = new Audio(`./Resources/${modelDir}/${voice.sound}`);
    currentVoice.volume = voiceVolume;
    currentVoice.addEventListener('ended', () => {
      currentVoice = null;
    });
    currentVoice.play().catch(() => {
      currentVoice = null;
    });
  };

  const selectStoryVoice = (cue: StoryCue): LocalizedVoice | undefined => {
    if (cue.voices?.[currentLanguage]) {
      return cue.voices[currentLanguage];
    }

    return cue.voices?.cn;
  };

  const storyCueMetaLabel = (cue: StoryCue): string => {
    const speaker = cue.speaker.trim();
    const step = cue.step > 0 ? `步骤 ${cue.step}` : '';
    return [speaker, step].filter(Boolean).join(' · ') || '剧情语音';
  };

  const isStoryPlaybackActive = (): boolean => {
    return storySequenceRunning || storyCuePlaying || activeStoryCue != null;
  };

  const selectedStorySegment = (): StorySegment | null => {
    return storySegments.find(segment => segment.id === selectedStorySegmentId) || storySegments[0] || null;
  };

  const storyMotionKey = (motion: MotionButtonConfig | undefined): string => {
    if (!motion) {
      return '';
    }
    return motion.actionId || `${motion.group}:${motion.index}`;
  };

  const isStoryLoopMotion = (motion: MotionButtonConfig): boolean => {
    const value = `${motion.actionId} ${motion.label} ${motion.group}`.toLowerCase();
    return /(?:^|[_\s-])(?:loop|idle)(?:$|[_\s-])/.test(value) || value.endsWith('_loop');
  };

  const syncStoryControls = (): void => {
    const segment = selectedStorySegment();
    const available = Boolean(segment?.cues.length);
    const active = isStoryPlaybackActive();

    if (storyPlayButton) {
      storyPlayButton.disabled = !available;
      storyPlayButton.classList.toggle('active', storySequenceRunning);
      storyPlayButton.textContent = storySequenceRunning ? '播放中' : '连续播放';
    }
    if (storyStopButton) {
      storyStopButton.disabled = !active;
      storyStopButton.classList.toggle('active', active);
    }
  };

  const updateStoryCueHighlight = (scroll = false): void => {
    if (!storyCueList) {
      return;
    }

    const buttons = Array.from(storyCueList.querySelectorAll<HTMLButtonElement>('.story-cue-button'));
    let activeButton: HTMLButtonElement | null = null;
    buttons.forEach(button => {
      const active = activeStorySegmentId === selectedStorySegmentId &&
        Number(button.dataset.cueIndex) === activeStoryCueIndex;
      button.classList.toggle('active', active);
      button.setAttribute('aria-current', active ? 'true' : 'false');
      if (active) {
        activeButton = button;
      }
    });

    if (scroll && activeButton) {
      activeButton.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    }
  };

  const clearStoryActiveState = (): void => {
    storySequenceRunning = false;
    storyCuePlaying = false;
    activeStoryCue = null;
    activeStorySegmentId = '';
    activeStoryCueIndex = -1;
    lastStoryMotionKey = '';
    updateStoryCueHighlight();
    syncStoryControls();
  };

  const cancelStoryPlayback = (): void => {
    storyPlaybackToken += 1;
    stopVoice();
    LAppDelegate.getInstance().setManualMotionLoop(false);
    clearStoryActiveState();
  };

  const storyTextWaitMs = (cue: StoryCue): number => {
    const characterCount = Array.from(cue.subtitle || '').length;
    return Math.max(1200, Math.min(9000, 900 + characterCount * 95));
  };

  const waitForStoryCue = (cue: StoryCue, token: number): Promise<void> => {
    const voice = selectStoryVoice(cue);
    const localizedVoice = voice
      ? { ...voice, subtitle: voice.subtitle || cue.subtitle }
      : { subtitle: cue.subtitle };
    showSubtitle(localizedVoice, storyCueMetaLabel(cue));

    return new Promise(resolve => {
      let settled = false;
      let timer = 0;

      const finish = (): void => {
        if (settled) {
          return;
        }
        settled = true;
        if (timer != 0) {
          window.clearTimeout(timer);
          timer = 0;
        }
        if (currentVoiceFinish === finish) {
          currentVoiceFinish = null;
        }
        resolve();
      };

      const scheduleFinish = (delay: number): void => {
        if (timer != 0) {
          window.clearTimeout(timer);
        }
        timer = window.setTimeout(finish, Math.max(0, delay));
      };

      currentVoiceFinish = finish;
      if (token !== storyPlaybackToken) {
        finish();
        return;
      }

      if (!voice?.sound) {
        scheduleFinish(storyTextWaitMs(cue));
        return;
      }

      const modelDir = currentModelId || LAppDefine.ModelDir[0];
      const audio = new Audio(`./Resources/${modelDir}/${voice.sound}`);
      currentVoice = audio;
      audio.volume = voiceVolume;

      const releaseAudio = (): void => {
        if (currentVoice === audio) {
          currentVoice = null;
        }
      };
      audio.addEventListener('ended', () => {
        releaseAudio();
        finish();
      }, { once: true });
      audio.addEventListener('error', () => {
        releaseAudio();
        scheduleFinish(storyTextWaitMs(cue));
      }, { once: true });
      audio.addEventListener('loadedmetadata', () => {
        if (Number.isFinite(audio.duration) && audio.duration > 0) {
          scheduleFinish(Math.max(storyTextWaitMs(cue), Math.ceil(audio.duration * 1000) + 2000));
        }
      }, { once: true });

      scheduleFinish(60000);
      audio.play().catch(() => {
        releaseAudio();
        scheduleFinish(storyTextWaitMs(cue));
      });
    });
  };

  const playStoryMotion = (cue: StoryCue): void => {
    const motion = cue.motion;
    const key = storyMotionKey(motion);
    if (!motion || !key || key === lastStoryMotionKey) {
      return;
    }

    const detail = resolveMotionDetail(motion);
    if (!detail) {
      return;
    }

    lastStoryMotionKey = key;
    currentManualDetail = null;
    lastVoiceDetail = null;
    const loop = isStoryLoopMotion(motion);
    LAppDelegate.getInstance().setManualMotionLoop(false);
    LAppDelegate.getInstance().playMotion(detail.group, detail.index, {
      loop,
      nativeLoop: loop,
      actionId: detail.actionId,
      label: detail.label,
      duration: detail.duration,
      ambientMotion: ambientMotionForDetail(detail)
    });
    syncExportMotionButton();
  };

  const activateStoryCue = (
    segment: StorySegment,
    cue: StoryCue,
    cueIndex: number
  ): void => {
    selectedStorySegmentId = segment.id;
    if (storySegmentSelect) {
      storySegmentSelect.value = segment.id;
    }
    activeStoryCue = cue;
    activeStorySegmentId = segment.id;
    activeStoryCueIndex = cueIndex;
    storyTapNextCueBySegment.set(segment.id, (cueIndex + 1) % segment.cues.length);
    storyCuePlaying = true;
    currentManualDetail = null;
    lastVoiceDetail = null;
    updateStoryCueHighlight(true);
    syncStoryControls();
  };

  const playStoryCueAndWait = async (
    segment: StorySegment,
    cueIndex: number,
    token: number
  ): Promise<void> => {
    const cue = segment.cues[cueIndex];
    if (!cue || token !== storyPlaybackToken) {
      return;
    }

    activateStoryCue(segment, cue, cueIndex);
    playStoryMotion(cue);
    await waitForStoryCue(cue, token);
  };

  const finishStoryPlayback = (token: number): void => {
    if (token !== storyPlaybackToken) {
      return;
    }

    stopVoice();
    clearStoryActiveState();
    currentManualDetail = null;
    lastVoiceDetail = null;
    hideSubtitle();
    syncExportMotionButton();
    LAppDelegate.getInstance().setManualMotionLoop(false);
    LAppDelegate.getInstance().returnToIdle();
  };

  const startStorySequence = async (
    segment: StorySegment,
    startIndex = 0
  ): Promise<void> => {
    cancelStoryPlayback();
    const token = storyPlaybackToken;
    storySequenceRunning = true;
    storyCuePlaying = true;
    lastStoryMotionKey = '';
    syncStoryControls();

    for (let cueIndex = Math.max(0, startIndex); cueIndex < segment.cues.length; cueIndex += 1) {
      if (token !== storyPlaybackToken) {
        return;
      }
      await playStoryCueAndWait(segment, cueIndex, token);
    }

    finishStoryPlayback(token);
  };

  const startSingleStoryCue = async (
    segment: StorySegment,
    cueIndex: number
  ): Promise<void> => {
    cancelStoryPlayback();
    const token = storyPlaybackToken;
    storySequenceRunning = false;
    storyCuePlaying = true;
    lastStoryMotionKey = '';
    syncStoryControls();
    await playStoryCueAndWait(segment, cueIndex, token);
    finishStoryPlayback(token);
  };

  const startStoryTouchAction = async (action: StoryTouchAction): Promise<void> => {
    const subtitles = (action.subtitles || []).filter(Boolean);
    if (subtitles.length === 0) {
      return;
    }

    cancelStoryPlayback();
    const token = storyPlaybackToken;
    const subtitle = subtitles[Math.floor(Math.random() * subtitles.length)];
    const voices: Partial<Record<VoiceLanguage, LocalizedVoice>> = {};
    (['cn', 'en'] as VoiceLanguage[]).forEach(language => {
      const source = action.voices?.[language];
      const soundCandidates = (source?.sounds || []).filter(Boolean);
      const sound = soundCandidates.length > 0
        ? soundCandidates[Math.floor(Math.random() * soundCandidates.length)]
        : source?.sound;
      voices[language] = { ...(source || {}), ...(sound ? { sound } : {}), subtitle };
    });
    const cue: StoryCue = {
      step: action.storyStep,
      action: 'storygame-touch',
      speaker: currentModelConfig?.title || '区域点触',
      subtitle,
      voiceId: action.audioId,
      audioName: action.audioName,
      motion: action.motion,
      voices
    };

    storySequenceRunning = false;
    storyCuePlaying = true;
    activeStoryCue = cue;
    activeStorySegmentId = '';
    activeStoryCueIndex = -1;
    lastStoryMotionKey = '';
    currentManualDetail = null;
    lastVoiceDetail = null;
    updateStoryCueHighlight();
    syncStoryControls();
    playStoryMotion(cue);
    await waitForStoryCue(cue, token);
    finishStoryPlayback(token);
  };

  const renderStoryCueList = (segment: StorySegment | null): void => {
    if (!storyCueList) {
      return;
    }

    storyCueList.innerHTML = '';
    if (!segment) {
      return;
    }

    segment.cues.forEach((cue, cueIndex) => {
      const button = document.createElement('button');
      button.className = 'story-cue-button';
      button.type = 'button';
      button.dataset.cueIndex = String(cueIndex);
      button.setAttribute('role', 'listitem');
      button.setAttribute('aria-current', 'false');
      button.title = cue.audioName || cue.voiceId || `步骤 ${cue.step}`;

      const speaker = document.createElement('span');
      speaker.className = 'story-cue-speaker';
      speaker.textContent = cue.speaker || '剧情对白';
      button.appendChild(speaker);

      const step = document.createElement('span');
      step.className = 'story-cue-step';
      step.textContent = cue.step > 0 ? `#${cue.step}` : '';
      button.appendChild(step);

      const text = document.createElement('span');
      text.className = 'story-cue-text';
      text.textContent = cue.subtitle || cue.audioName || cue.voiceId;
      button.appendChild(text);

      button.addEventListener('pointerdown', event => event.stopPropagation());
      button.addEventListener('pointerup', event => event.stopPropagation());
      button.addEventListener('click', event => {
        event.stopPropagation();
        void startSingleStoryCue(segment, cueIndex);
      });
      storyCueList.appendChild(button);
    });

    updateStoryCueHighlight();
  };

  const renderStoryPerformance = (config: ModelConfig): void => {
    storySegments = (config.storySegments || []).filter(segment => Array.isArray(segment.cues) && segment.cues.length > 0);
    const available = storySegments.length > 0;
    if (storyPerformance) {
      storyPerformance.hidden = !available;
    }

    if (!available) {
      selectedStorySegmentId = '';
      if (storySegmentSelect) {
        storySegmentSelect.innerHTML = '';
      }
      if (storyPerformanceCount) {
        storyPerformanceCount.textContent = '';
      }
      renderStoryCueList(null);
      syncStoryControls();
      return;
    }

    if (!storySegments.some(segment => segment.id === selectedStorySegmentId)) {
      selectedStorySegmentId = storySegments[0].id;
    }

    if (storySegmentSelect) {
      storySegmentSelect.innerHTML = '';
      storySegments.forEach((segment, index) => {
        const option = document.createElement('option');
        option.value = segment.id;
        option.textContent = `${segment.label || `片段 ${index + 1}`} · ${segment.cues.length} 句`;
        option.selected = segment.id === selectedStorySegmentId;
        storySegmentSelect.appendChild(option);
      });
      storySegmentSelect.value = selectedStorySegmentId;
    }

    if (storyPerformanceCount) {
      const cueCount = storySegments.reduce((total, segment) => total + segment.cues.length, 0);
      storyPerformanceCount.textContent = `${storySegments.length} 段 · ${cueCount} 句`;
    }
    renderStoryCueList(selectedStorySegment());
    syncStoryControls();
  };

  const resetPanel = (): void => {
    cancelStoryPlayback();
    cancelProgress();
    lastVoiceDetail = null;
    currentManualDetail = null;
    LAppDelegate.getInstance().setManualMotionLoop(false);
    syncExportMotionButton();
    allMotionButtons().forEach(item => item.classList.remove('active'));

    if (statusName) {
      statusName.textContent = '自由点触';
    }

    if (statusTime) {
      statusTime.textContent = '0.0 / 0.0s';
    }

    if (progressFill) {
      progressFill.style.width = '0%';
    }

    hideSubtitle();
  };

  const updateProgress = (): void => {
    const elapsed = Math.max(0, (performance.now() - progressStart) / 1000);
    const clamped = Math.min(elapsed, progressDuration);
    const ratio = progressDuration > 0 ? clamped / progressDuration : 0;

    if (statusTime) {
      statusTime.textContent = `${clamped.toFixed(1)} / ${progressDuration.toFixed(1)}s`;
    }

    if (progressFill) {
      progressFill.style.width = `${Math.round(ratio * 1000) / 10}%`;
    }

    if (clamped < progressDuration) {
      progressFrame = requestAnimationFrame(updateProgress);
    }
  };

  const startMotionStatus = (detail: MotionDetail): void => {
    cancelProgress();
    progressStart = performance.now();
    progressDuration = detail.duration;

    if (statusName) {
      statusName.textContent = detail.label;
    }

    updateProgress();
  };

  const detailFromButton = (button: HTMLButtonElement): MotionDetail | null => {
    const group = button.dataset.group;
    const index = Number(button.dataset.index);

    if (!group || Number.isNaN(index)) {
      return null;
    }

    return {
      group,
      index,
      actionId:
        button.dataset.actionId ||
        `${group}:${index}:${button.dataset.label || button.textContent || ''}`,
      label: button.dataset.label || button.textContent || '',
      duration: Number(button.dataset.duration || '0')
    };
  };

  const highlightMotion = (detail: MotionDetail): void => {
    allMotionButtons().forEach(item => {
      const isActive = detail.actionId
        ? item.dataset.actionId === detail.actionId
        : item.dataset.group === detail.group &&
          Number(item.dataset.index) === detail.index;
      item.classList.toggle('active', isActive);
    });
  };

  const manualPlaybackFromDetail = (detail: MotionDetail) => ({
    group: detail.group,
    index: detail.index,
    actionId: detail.actionId,
    label: detail.label,
    duration: detail.duration
  });

  const ambientMotionForDetail = (detail: MotionDetail): MotionButtonConfig | null => {
    const ambient = currentModelConfig?.ambientMotion;
    if (!ambient) {
      return null;
    }
    if (detail.actionId && ambient.actionId === detail.actionId) {
      return null;
    }
    if (ambient.group === detail.group && ambient.index === detail.index) {
      return null;
    }
    return ambient;
  };

  const resolveMotionDetail = (detail: Partial<MotionDetail> | undefined): MotionDetail | null => {
    if (!detail?.group || typeof detail.index !== 'number') {
      return null;
    }

    const matchingButton = allMotionButtons().find(button => {
      return button.dataset.group === detail.group &&
        Number(button.dataset.index) === detail.index &&
        (!detail.actionId || button.dataset.actionId === detail.actionId);
    });
    const buttonDetail = matchingButton ? detailFromButton(matchingButton) : null;

    return {
      group: detail.group,
      index: detail.index,
      actionId: detail.actionId || buttonDetail?.actionId,
      label: detail.label || buttonDetail?.label || `${detail.group}:${detail.index}`,
      duration: typeof detail.duration === 'number' ? detail.duration : buttonDetail?.duration || 0,
      voices: detail.voices || buttonDetail?.voices
    };
  };

  const setRecordStatus = (message: string): void => {
    if (recordStatus) {
      recordStatus.textContent = message;
    }
  };

  const viewerCanvas = (): HTMLCanvasElement | null => {
    return document.querySelector<HTMLCanvasElement>('body > canvas') ||
      document.querySelector<HTMLCanvasElement>('canvas');
  };

  const recordingResolutionLabel = (canvas = viewerCanvas()): string => {
    return canvas ? `${canvas.width}x${canvas.height}` : '';
  };

  const motionExportPixelScale = (): number => {
    const canvas = viewerCanvas();
    const cssWidth = Math.max(1, canvas?.clientWidth || canvas?.width || 1920);
    const cssHeight = Math.max(1, canvas?.clientHeight || canvas?.height || 1080);
    const baseRatio = Math.max(1, window.devicePixelRatio || 1);
    const maxScaleBySide = 4096 / Math.max(cssWidth * baseRatio, cssHeight * baseRatio);
    return Math.max(1, Math.min(2, maxScaleBySide));
  };

  const applyMotionExportPixelScale = (scale: number): void => {
    activeMotionExportPixelScale = Math.max(1, scale);
    (window as RenderScaleWindow).__ronLive2DExportPixelScale = activeMotionExportPixelScale;
    LAppDelegate.getInstance().onResize();
  };

  const restoreMotionExportPixelScale = (): void => {
    if (activeMotionExportPixelScale === 1) {
      return;
    }

    activeMotionExportPixelScale = 1;
    (window as RenderScaleWindow).__ronLive2DExportPixelScale = 1;
    LAppDelegate.getInstance().onResize();
  };

  const isRecordingActive = (): boolean => {
    return mediaRecorder != null && mediaRecorder.state !== 'inactive';
  };

  const recordingFrameRate = (mode: RecordingMode): number => {
    return mode === 'motion' ? 30 : 60;
  };

  const recordingVideoBitsPerSecond = (mode: RecordingMode): number => {
    const canvas = viewerCanvas();
    const pixels = Math.max(1920 * 1080, (canvas?.width || 0) * (canvas?.height || 0));

    if (mode === 'motion') {
      return Math.max(36000000, Math.min(100000000, Math.round(pixels * 10)));
    }

    return Math.max(36000000, Math.min(120000000, Math.round(pixels * 12)));
  };

  const chooseRecordingType = (mode: RecordingMode = 'manual'): RecordingType => {
    const fallback: RecordingType = { mimeType: '', extension: 'webm', label: 'WebM' };

    if (typeof MediaRecorder === 'undefined') {
      return fallback;
    }

    const smoothPlaybackCandidates: RecordingType[] = [
      { mimeType: 'video/mp4;codecs=avc1.640028', extension: 'mp4', label: 'MP4' },
      { mimeType: 'video/mp4;codecs=avc1.42E01E', extension: 'mp4', label: 'MP4' },
      { mimeType: 'video/mp4', extension: 'mp4', label: 'MP4' },
      { mimeType: 'video/webm;codecs=vp9', extension: 'webm', label: 'WebM' },
      { mimeType: 'video/webm;codecs=vp8', extension: 'webm', label: 'WebM' },
      { mimeType: 'video/webm', extension: 'webm', label: 'WebM' }
    ];
    const crispPreviewCandidates: RecordingType[] = [
      { mimeType: 'video/webm;codecs=vp9', extension: 'webm', label: 'WebM' },
      { mimeType: 'video/webm;codecs=vp8', extension: 'webm', label: 'WebM' },
      { mimeType: 'video/webm', extension: 'webm', label: 'WebM' },
      { mimeType: 'video/mp4;codecs=avc1.42E01E', extension: 'mp4', label: 'MP4' },
      { mimeType: 'video/mp4', extension: 'mp4', label: 'MP4' }
    ];
    const candidates = mode === 'motion' ? smoothPlaybackCandidates : crispPreviewCandidates;

    return candidates.find(candidate => MediaRecorder.isTypeSupported(candidate.mimeType)) || fallback;
  };

  const formatRecordingTime = (): string => {
    const elapsedSeconds = Math.max(0, Math.floor((performance.now() - recordingStartedAt) / 1000));
    const minutes = Math.floor(elapsedSeconds / 60);
    const seconds = elapsedSeconds % 60;
    return `${minutes}:${seconds < 10 ? '0' : ''}${seconds}`;
  };

  const syncRecordingButton = (recording: boolean): void => {
    if (!recordButton) {
      return;
    }

    recordButton.textContent = recording
      ? (recordingMode === 'motion' ? '导出中' : '停止并保存')
      : '手动录制';
    recordButton.disabled = recording && recordingMode === 'motion';
    recordButton.classList.toggle('recording', recording);
  };

  const syncExportMotionButton = (): void => {
    const canvas = viewerCanvas();
    const supported = typeof MediaRecorder !== 'undefined' &&
      typeof canvas?.captureStream === 'function';
    const recording = isRecordingActive();
    const canExport = supported && currentManualDetail != null && !recording && !motionExportPreparing;

    if (exportMotionButton) {
      exportMotionButton.disabled = !canExport;
      exportMotionButton.textContent = motionExportPreparing ? '准备中' : (recording && recordingMode === 'motion' ? '导出中' : '导出动作');
      exportMotionButton.title = !supported
        ? '当前浏览器不支持 canvas 导出'
        : (currentManualDetail ? '从头播放并以高分辨率导出当前选中的动作' : '先选择一个动作');
      exportMotionButton.classList.toggle('recording', recording && recordingMode === 'motion');
    }

    if (offlineExportButton) {
      offlineExportButton.hidden = !localOfflineExportAvailable;
      offlineExportButton.disabled = !localOfflineExportAvailable || currentManualDetail == null || recording || motionExportPreparing;
      offlineExportButton.title = localOfflineExportAvailable
        ? (currentManualDetail ? '本地后台逐帧渲染当前动作并用 ffmpeg 合成 MP4' : '先选择一个动作')
        : '离线导出只在本地管理器可用';
    }
  };

  const syncRecordingIdle = (status = '未导出'): void => {
    const canvas = viewerCanvas();
    const supported = typeof MediaRecorder !== 'undefined' &&
      typeof canvas?.captureStream === 'function';

    if (recordButton) {
      recordButton.disabled = !supported;
      recordButton.title = supported
        ? '手动录制当前画面；浏览器支持 MP4 时保存 MP4，否则保存 WebM'
        : '当前浏览器不支持 canvas 录制';
    }

    syncRecordingButton(false);
    syncExportMotionButton();
    setRecordStatus(supported ? status : '无法导出');
  };

  const updateRecordingStatus = (): void => {
    const resolution = recordingResolution ? ` · ${recordingResolution}` : '';
    const frameRate = recordingFrameRateValue ? ` · ${recordingFrameRateValue}fps` : '';
    setRecordStatus(`${recordingMode === 'motion' ? '导出动作中' : '录制中'} ${formatRecordingTime()}${resolution}${frameRate}`);
  };

  const clearRecordingTimer = (): void => {
    if (recordingTimer != 0) {
      window.clearInterval(recordingTimer);
      recordingTimer = 0;
    }
  };

  const clearMotionExportTimer = (): void => {
    if (motionExportStopTimer != 0) {
      window.clearTimeout(motionExportStopTimer);
      motionExportStopTimer = 0;
    }
  };

  const cleanupRecordingStream = (): void => {
    recordingStream?.getTracks().forEach(track => track.stop());
    recordingStream = null;
  };

  const waitForAnimationFrames = (frames = 1): Promise<void> => {
    return new Promise(resolve => {
      let remaining = Math.max(1, frames);
      const tick = (): void => {
        remaining -= 1;
        if (remaining <= 0) {
          resolve();
          return;
        }
        window.requestAnimationFrame(tick);
      };
      window.requestAnimationFrame(tick);
    });
  };

  const fileNamePart = (value: string | undefined, fallback: string): string => {
    const cleaned = (value || fallback)
      .replace(/[\\/:*?"<>|]+/g, '_')
      .replace(/\s+/g, '_')
      .replace(/^_+|_+$/g, '');
    return cleaned || fallback;
  };

  const recordingFileName = (extension: string, detail?: MotionDetail | null): string => {
    const baseName = fileNamePart(currentModelId || 'live2d', 'live2d');
    const motionName = detail
      ? `_${fileNamePart(detail.label || detail.actionId || `${detail.group}_${detail.index}`, 'motion')}`
      : '';
    const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
    return `RON_Live2D_${baseName}${motionName}_${timestamp}.${extension}`;
  };

  const finishRecording = (): void => {
    clearRecordingTimer();
    clearMotionExportTimer();

    const recorder = mediaRecorder;
    const completedMode = recordingMode;
    const completedMotion = recordingMotionDetail;
    const completedResolution = recordingResolution;
    const completedFrameRate = recordingFrameRateValue;
    const mimeType = recorder?.mimeType || recordingMimeType || 'video/webm';
    const extension = mimeType.includes('mp4') ? 'mp4' : recordingExtension;
    const chunks = recordingChunks;

    mediaRecorder = null;
    recordingMode = null;
    recordingMotionDetail = null;
    recordingChunks = [];
    recordingResolution = '';
    recordingFrameRateValue = 0;
    motionExportPreparing = false;
    cleanupRecordingStream();
    if (completedMode === 'motion') {
      restoreMotionExportPixelScale();
    }

    if (chunks.length === 0) {
      syncRecordingIdle('没有录到画面');
      syncMotionLoopToggle();
      return;
    }

    const blob = new Blob(chunks, { type: mimeType });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = recordingFileName(extension, completedMotion);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.setTimeout(() => URL.revokeObjectURL(url), 10000);
    syncRecordingIdle(`${completedMode === 'motion' ? '动作已导出' : '已保存'} ${extension.toUpperCase()}${completedResolution ? ` · ${completedResolution}` : ''}${completedFrameRate ? ` · ${completedFrameRate}fps` : ''}`);
    syncMotionLoopToggle();
  };

  const stopRecording = (): void => {
    if (!mediaRecorder || mediaRecorder.state === 'inactive') {
      return;
    }

    mediaRecorder.stop();
  };

  const startRecording = (mode: RecordingMode = 'manual', detail?: MotionDetail | null): boolean => {
    const canvas = viewerCanvas();

    if (isRecordingActive()) {
      return false;
    }

    if (typeof MediaRecorder === 'undefined' || !canvas || typeof canvas.captureStream !== 'function') {
      syncRecordingIdle('无法导出');
      return false;
    }

    const frameRate = recordingFrameRate(mode);
    const recordingType = chooseRecordingType(mode);
    const recorderOptions = {
      ...(recordingType.mimeType ? { mimeType: recordingType.mimeType } : {}),
      videoBitsPerSecond: recordingVideoBitsPerSecond(mode)
    };
    recordingExtension = recordingType.extension;
    recordingMimeType = recordingType.mimeType || 'video/webm';
    recordingMode = mode;
    recordingMotionDetail = detail || null;
    recordingChunks = [];
    recordingResolution = recordingResolutionLabel(canvas);
    recordingFrameRateValue = frameRate;

    try {
      recordingStream = canvas.captureStream(frameRate);
      mediaRecorder = new MediaRecorder(recordingStream, recorderOptions);
    } catch {
      mediaRecorder = null;
      recordingMode = null;
      recordingMotionDetail = null;
      recordingResolution = '';
      recordingFrameRateValue = 0;
      motionExportPreparing = false;
      cleanupRecordingStream();
      syncRecordingIdle('导出失败');
      return false;
    }

    mediaRecorder.addEventListener('dataavailable', event => {
      if (event.data.size > 0) {
        recordingChunks.push(event.data);
      }
    });
    mediaRecorder.addEventListener('stop', finishRecording, { once: true });
    mediaRecorder.addEventListener('error', () => {
      const failedMode = recordingMode;
      clearRecordingTimer();
      clearMotionExportTimer();
      cleanupRecordingStream();
      mediaRecorder = null;
      recordingMode = null;
      recordingMotionDetail = null;
      recordingChunks = [];
      recordingResolution = '';
      recordingFrameRateValue = 0;
      motionExportPreparing = false;
      if (failedMode === 'motion') {
        restoreMotionExportPixelScale();
      }
      syncRecordingIdle('导出失败');
    }, { once: true });

    recordingStartedAt = performance.now();
    mediaRecorder.start(mode === 'motion' ? 250 : 120);
    recordingTimer = window.setInterval(updateRecordingStatus, 250);
    syncRecordingButton(true);
    syncExportMotionButton();
    updateRecordingStatus();
    return true;
  };

  const startMotionExport = async (): Promise<void> => {
    const detail = currentManualDetail;

    if (!detail) {
      syncRecordingIdle('先选择动作');
      return;
    }

    if (motionExportPreparing || isRecordingActive()) {
      return;
    }

    const exportLoop = motionLoopEnabled;
    motionExportPreparing = true;
    stopVoice();
    hideSubtitle();
    lastVoiceDetail = null;
    LAppDelegate.getInstance().setManualMotionLoop(false);
    applyMotionExportPixelScale(motionExportPixelScale());
    setRecordStatus('准备高清导出');
    syncExportMotionButton();
    await waitForAnimationFrames(2);

    if (currentManualDetail !== detail) {
      motionExportPreparing = false;
      restoreMotionExportPixelScale();
      syncRecordingIdle('导出已取消');
      return;
    }

    if (!startRecording('motion', detail)) {
      motionExportPreparing = false;
      restoreMotionExportPixelScale();
      return;
    }

    motionExportPreparing = false;
    syncExportMotionButton();
    LAppDelegate.getInstance().playMotion(detail.group, detail.index, {
      loop: exportLoop,
      actionId: detail.actionId,
      label: detail.label,
      duration: detail.duration,
      ambientMotion: ambientMotionForDetail(detail)
    });
    const motionDurationMs = Math.ceil(Math.max(0, detail.duration) * 1000);
    const exportDurationMs = exportLoop
      ? Math.max(900, motionDurationMs + 120)
      : Math.max(900, motionDurationMs + 700);
    motionExportStopTimer = window.setTimeout(() => {
      if (recordingMode === 'motion') {
        stopRecording();
      }
    }, exportDurationMs);
  };

  const offlineExportOutputLabel = (output: string): string => {
    const match = /(?:^|\n)output=(.+)/u.exec(output || '');
    if (!match) {
      return '';
    }
    return match[1].trim().replace(/\\/g, '/');
  };

  const offlineExportProgressLabel = (output: string): string => {
    const pattern = /progress=(\d+)\/(\d+)/gu;
    let match: RegExpExecArray | null = null;
    let last: RegExpExecArray | null = null;
    while ((match = pattern.exec(output || '')) != null) {
      last = match;
    }
    if (!last) {
      return '';
    }
    return `${last[1]}/${last[2]}`;
  };

  const pollOfflineExportTask = (taskId: string): void => {
    const tick = async (): Promise<void> => {
      try {
        const response = await fetch(`/api/task?id=${encodeURIComponent(taskId)}`);
        if (!response.ok) {
          throw new Error(await response.text());
        }
        const task = await response.json() as { status?: string; output?: string; error?: string };
        const progress = offlineExportProgressLabel(task.output || '');
        if (task.status === 'ok') {
          const output = offlineExportOutputLabel(task.output || '');
          setRecordStatus(output ? `离线导出完成 · ${output}` : '离线导出完成');
          return;
        }
        if (task.status === 'error') {
          setRecordStatus(task.error ? `离线导出失败 · ${task.error}` : '离线导出失败');
          return;
        }
        setRecordStatus(progress ? `离线导出中 · ${progress}` : '离线导出中');
        window.setTimeout(tick, 1000);
      } catch (error) {
        console.error(error);
        setRecordStatus('离线导出状态读取失败');
      }
    };
    window.setTimeout(tick, 800);
  };

  const startOfflineMotionExport = async (): Promise<void> => {
    const detail = currentManualDetail;

    if (!detail || !localOfflineExportAvailable) {
      syncRecordingIdle(detail ? '离线导出仅本地可用' : '先选择动作');
      return;
    }

    const canvas = viewerCanvas();
    const payload = {
      action: 'offline_motion_export',
      model_id: currentModelId,
      group: detail.group,
      index: detail.index,
      action_id: detail.actionId || '',
      label: detail.label || '',
      duration: detail.duration || 0,
      loop: motionLoopEnabled,
      hide_protagonist: hideProtagonist,
      scale: 1.0,
      width: canvas?.width || 1920,
      height: canvas?.height || 1080,
      pixel_ratio: 1,
      language: currentLanguage
    };

    try {
      setRecordStatus('已提交离线导出');
      syncExportMotionButton();
      const response = await fetch('/api/tasks/live2d-offline-motion-export', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      if (!response.ok) {
        const text = await response.text();
        let message = text || `HTTP ${response.status}`;
        try {
          const data = JSON.parse(text) as { error?: string };
          message = data.error || message;
        } catch {
          // Keep the raw response text.
        }
        throw new Error(message);
      }
      const task = await response.json() as { id?: string };
      setRecordStatus(task.id ? `离线导出任务已开始 · ${task.id}` : '离线导出任务已开始');
      if (task.id) {
        pollOfflineExportTask(task.id);
      }
    } catch (error) {
      console.error(error);
      const message = error instanceof Error ? error.message : String(error);
      setRecordStatus(message ? `离线导出启动失败 · ${message.slice(0, 120)}` : '离线导出启动失败');
    } finally {
      syncExportMotionButton();
    }
  };

  const installOfflineExportBridge = (): void => {
    (window as typeof window & {
      __ronLive2DOfflineExport?: {
        ready: () => boolean;
        modelId: () => string;
        modelCanvasSize: () => OfflineExportModelSize | null;
        drawableBounds: () => OfflineExportDrawableBounds | null;
        drawableBoundsRaw: () => OfflineExportDrawableBounds | null;
        resize: () => void;
        play: (detail: Partial<MotionDetail> & { group: string; index: number; loop?: boolean; hideProtagonist?: boolean; scale?: number }) => void;
        step: (deltaTimeSeconds: number) => OfflineExportFrame | null;
        advance: (deltaTimeSeconds: number) => void;
        setPanelHidden: (hidden: boolean) => void;
      };
    }).__ronLive2DOfflineExport = {
      ready: () => live2DModelReady && viewerCanvas() != null,
      modelId: () => currentModelId,
      modelCanvasSize: () => LAppDelegate.getInstance().getModelCanvasSize(),
      drawableBounds: () => LAppDelegate.getInstance().getDrawableBounds(true),
      drawableBoundsRaw: () => LAppDelegate.getInstance().getDrawableBounds(false),
      resize: () => {
        LAppDelegate.getInstance().onResize();
        LAppDelegate.getInstance().renderFrame();
      },
      play: detail => {
        const resolved = resolveMotionDetail(detail) || {
          group: detail.group,
          index: detail.index,
          actionId: detail.actionId,
          label: detail.label || `${detail.group}:${detail.index}`,
          duration: detail.duration || 0
        };
        cancelStoryPlayback();
        stopVoice();
        hideSubtitle();
        currentManualDetail = resolved;
        hideProtagonist = detail.hideProtagonist === true;
        viewZoom = clampViewZoom(Number(detail.scale || viewZoom));
        window.dispatchEvent(new CustomEvent('live2d-view-scale-changed', { detail: { scale: viewZoom } }));
        syncProtagonistToggle();
        LAppDelegate.getInstance().setManualMotionLoop(false);
        LAppDelegate.getInstance().playMotion(resolved.group, resolved.index, {
          loop: detail.loop === true,
          nativeLoop: detail.loop === true,
          actionId: resolved.actionId,
          label: resolved.label,
          duration: resolved.duration,
          ambientMotion: ambientMotionForDetail(resolved)
        });
      },
      advance: deltaTimeSeconds => {
        LAppDelegate.getInstance().setFixedDeltaTime(Math.max(0, deltaTimeSeconds));
        LAppDelegate.getInstance().renderFrame();
        LAppDelegate.getInstance().setFixedDeltaTime(null);
      },
      step: deltaTimeSeconds => {
        const canvas = viewerCanvas();
        if (!canvas) {
          return null;
        }
        LAppDelegate.getInstance().setFixedDeltaTime(Math.max(0, deltaTimeSeconds));
        LAppDelegate.getInstance().renderFrame();
        LAppDelegate.getInstance().setFixedDeltaTime(null);
        return {
          dataUrl: canvas.toDataURL('image/png'),
          width: canvas.width,
          height: canvas.height
        };
      },
      setPanelHidden: hidden => {
        togglePanelHidden(hidden);
      }
    };
  };

  if (isOfflineExportMode()) {
    installOfflineExportBridge();
  }

  const renderModelTabs = (eventDetail: ModelConfigEvent): void => {
    const versionTabs = document.querySelector<HTMLElement>('#version-tabs');
    if (!modelSelect || !stageTabs || !versionTabs) {
      return;
    }

    const stageSuffix = /\s+(一段|二段|三段|四段)$/u;
    const groups = new Map<string, { key: string; label: string; categoryLabel: string; assetKind?: string; items: Array<{ index: number; id: string; title: string; stage?: string; variantKey?: string; variantLabel?: string; variantSourceLayer?: string }> }>();
    eventDetail.models.forEach((model, index) => {
      const fallbackLabel = model.title.replace(stageSuffix, '').trim() || model.title;
      const categoryLabel = model.categoryLabel || (model.assetKind === 'storycg' ? '剧情CG' : (model.assetKind === 'character' || model.assetKind === 'skin' ? '角色' : '风格'));
      const keyBase = model.assetKind === 'storycg' || model.assetKind === 'character' || model.assetKind === 'skin' ? (model.modelDir || model.id) : (model.styleId || model.styleName || fallbackLabel || model.id);
      const key = `${model.assetKind || 'style'}:${keyBase}`;
      const label = model.styleName || fallbackLabel;
      const group = groups.get(key) || { key, label, categoryLabel, assetKind: model.assetKind, items: [] };
      group.items.push({
        index,
        id: model.id,
        title: model.title,
        stage: model.stage || model.title.replace(label, '').trim() || model.title,
        variantKey: model.variantKey || 'current',
        variantLabel: model.variantLabel || '当前版',
        variantSourceLayer: model.variantSourceLayer || ''
      });
      groups.set(key, group);
    });
    const stageRank = (stage?: string): number => {
      const value = stage || '';
      if (value.includes('一段')) return 1;
      if (value.includes('二段')) return 2;
      if (value.includes('三段')) return 3;
      if (value.includes('四段')) return 4;
      return 99;
    };
    const variantRank = (item: { variantKey?: string }): number => item.variantKey === 'current' ? 0 : 1;
    groups.forEach(group => {
      group.items.sort((left, right) => stageRank(left.stage) - stageRank(right.stage) || variantRank(left) - variantRank(right) || left.index - right.index);
    });
    modelPickerGroups = groups;
    const groupList = Array.from(groups.values()).sort((left, right) => {
      const kindRank = (group: { assetKind?: string }): number => group.assetKind === 'storycg' ? 1 : (group.assetKind === 'character' || group.assetKind === 'skin' ? 2 : 0);
      return kindRank(left) - kindRank(right) || left.label.localeCompare(right.label, 'zh-Hans-CN');
    });
    const selectedGroup = groupList.find(group => group.items.some(item => item.index === eventDetail.sceneIndex)) || groupList[0];

    modelSelect.innerHTML = '';
    const optionRoots = new Map<string, HTMLOptGroupElement | HTMLSelectElement>();
    groupList.forEach(group => {
      let optionRoot = optionRoots.get(group.categoryLabel);
      if (!optionRoot && groupList.some(item => item.categoryLabel !== group.categoryLabel)) {
        optionRoot = document.createElement('optgroup');
        optionRoot.label = group.categoryLabel;
        modelSelect.appendChild(optionRoot);
        optionRoots.set(group.categoryLabel, optionRoot);
      }
      if (!optionRoot) {
        optionRoot = modelSelect;
        optionRoots.set(group.categoryLabel, optionRoot);
      }
      const option = document.createElement('option');
      option.value = group.key;
      option.textContent = `${group.categoryLabel} · ${group.label}`;
      option.selected = selectedGroup?.key === group.key;
      optionRoot.appendChild(option);
    });
    if (selectedGroup) {
      modelSelect.value = selectedGroup.key;
    }

    const selectedItems = selectedGroup?.items || [];
    const selectedItem = selectedItems.find(item => item.index === eventDetail.sceneIndex) || selectedItems[0];
    const stages = new Map<string, Array<{ index: number; id: string; title: string; stage?: string; variantKey?: string; variantLabel?: string; variantSourceLayer?: string }>>();
    selectedItems.forEach(item => {
      const stage = item.stage || item.title;
      const list = stages.get(stage) || [];
      list.push(item);
      stages.set(stage, list);
    });
    const stageGroups = Array.from(stages.entries())
      .map(([stage, items]) => ({ stage, items: items.sort((left, right) => variantRank(left) - variantRank(right) || left.index - right.index) }))
      .sort((left, right) => stageRank(left.stage) - stageRank(right.stage));

    stageTabs.innerHTML = '';
    stageTabs.classList.toggle('single-stage', stageGroups.length <= 1);
    stageGroups.forEach(stageGroup => {
      const active = stageGroup.items.some(item => item.index === eventDetail.sceneIndex);
      const button = document.createElement('button');
      button.className = 'stage-button';
      button.type = 'button';
      button.textContent = stageGroup.stage;
      button.classList.toggle('active', active);
      button.addEventListener('pointerdown', pointerEvent =>
        pointerEvent.stopPropagation()
      );
      button.addEventListener('pointerup', pointerEvent =>
        pointerEvent.stopPropagation()
      );
      button.addEventListener('click', pointerEvent => {
        pointerEvent.stopPropagation();
        const next = stageGroup.items[0];
        if (!next || next.index === eventDetail.sceneIndex) {
          return;
        }
        resetPanel();
        stopVoice();
        LAppDelegate.getInstance().setScene(next.index);
      });
      stageTabs.appendChild(button);
    });

    const activeStageItems = stageGroups.find(stageGroup => stageGroup.items.some(item => item.index === eventDetail.sceneIndex))?.items || (selectedItem ? [selectedItem] : []);
    versionTabs.innerHTML = '';
    versionTabs.classList.toggle('single-version', activeStageItems.length <= 1);
    activeStageItems.forEach(item => {
      const button = document.createElement('button');
      button.className = 'version-button';
      button.type = 'button';
      button.textContent = item.variantLabel || '当前版';
      button.title = item.variantSourceLayer || item.id;
      button.classList.toggle('active', item.index === eventDetail.sceneIndex);
      button.addEventListener('pointerdown', pointerEvent => pointerEvent.stopPropagation());
      button.addEventListener('pointerup', pointerEvent => pointerEvent.stopPropagation());
      button.addEventListener('click', pointerEvent => {
        pointerEvent.stopPropagation();
        if (item.index === eventDetail.sceneIndex) {
          return;
        }
        resetPanel();
        stopVoice();
        LAppDelegate.getInstance().setScene(item.index);
      });
      versionTabs.appendChild(button);
    });
  };

  const renderUnlocks = (config: ModelConfig): void => {
    if (!unlockSection || !unlockRow) {
      return;
    }

    unlockRow.innerHTML = '';
    unlockSection.hidden = config.unlockableMotions.length === 0;

    config.unlockableMotions.forEach(action => {
      const button = document.createElement('button');
      button.className = 'unlock-toggle';
      button.dataset.value = action.actionId;
      button.type = 'button';
      button.setAttribute('aria-pressed', 'false');
      button.title = `允许自由点触抽到 motion: ${action.actionId}`;
      button.textContent = action.label;
      button.addEventListener('pointerdown', pointerEvent =>
        pointerEvent.stopPropagation()
      );
      button.addEventListener('pointerup', pointerEvent =>
        pointerEvent.stopPropagation()
      );
      button.addEventListener('click', pointerEvent => {
        pointerEvent.stopPropagation();
        const unlockedMotions = readUnlockedMotions(currentModelId);
        if (unlockedMotions.has(action.actionId)) {
          unlockedMotions.delete(action.actionId);
        } else {
          unlockedMotions.add(action.actionId);
        }
        writeUnlockedMotions(currentModelId, unlockedMotions);
        syncUnlockButtons();
      });
      unlockRow.appendChild(button);
    });

    syncUnlockButtons();
  };

  const renderMotionGroups = (config: ModelConfig): void => {
    if (!motionGroups) {
      return;
    }

    motionGroups.innerHTML = '';

    config.motionGroups.forEach(group => {
      const wrapper = document.createElement('div');
      wrapper.className = 'motion-group';
      if (group.actions.length === 1) {
        wrapper.classList.add('entry');
      }

      const label = document.createElement('div');
      label.className = 'motion-label';
      label.textContent = group.label;
      wrapper.appendChild(label);

      group.actions.forEach(action => {
        const button = document.createElement('button');
        button.className = 'motion-button';
        button.dataset.actionId = action.actionId;
        button.dataset.group = action.group;
        button.dataset.index = String(action.index);
        button.dataset.label = action.label;
        button.dataset.duration = String(action.duration);
        button.type = 'button';
        button.title = `motion: ${action.actionId}`;
        button.textContent = action.label;
        button.addEventListener('pointerdown', pointerEvent =>
          pointerEvent.stopPropagation()
        );
        button.addEventListener('pointerup', pointerEvent =>
          pointerEvent.stopPropagation()
        );
        button.addEventListener('click', pointerEvent => {
          pointerEvent.stopPropagation();
          const detail = detailFromButton(button);
          if (!detail) {
            return;
          }

          cancelStoryPlayback();
          stopVoice();
          hideSubtitle();
          lastVoiceDetail = null;
          currentManualDetail = detail;
          syncExportMotionButton();
          LAppDelegate.getInstance().playMotion(detail.group, detail.index, {
            loop: motionLoopEnabled,
            actionId: detail.actionId,
            label: detail.label,
            duration: detail.duration,
            ambientMotion: ambientMotionForDetail(detail)
          });
        });
        wrapper.appendChild(button);
      });

      motionGroups.appendChild(wrapper);
    });
  };

  const renderConfig = (eventDetail: ModelConfigEvent): void => {
    cancelStoryPlayback();
    currentModelConfig = eventDetail.config;
    currentModelId = eventDetail.config.id;
    currentHideGroups = eventDetail.config.hideGroups || [];
    hideProtagonist = readHideProtagonist(currentModelId);
    viewZoom = readViewZoom(currentModelId);
    syncViewZoom();
    syncProtagonistToggle();

    if (panelTitle) {
      panelTitle.textContent = eventDetail.config.title;
    }

    renderModelTabs(eventDetail);
    renderUnlocks(eventDetail.config);
    renderMotionGroups(eventDetail.config);
    renderStoryPerformance(eventDetail.config);
    resetPanel();
  };

  languageButtons.forEach(button => {
    button.addEventListener('pointerdown', event => event.stopPropagation());
    button.addEventListener('pointerup', event => event.stopPropagation());
    button.addEventListener('click', event => {
      event.stopPropagation();

      const nextLanguage = button.dataset.language;
      if (nextLanguage !== 'cn' && nextLanguage !== 'en') {
        return;
      }

      if (currentLanguage === nextLanguage) {
        return;
      }

      currentLanguage = nextLanguage;
      localStorage.setItem('live2d-voice-language', currentLanguage);
      syncLanguageButtons();

      if (activeStoryCue) {
        const segment = storySegments.find(item => item.id === activeStorySegmentId);
        const cueIndex = activeStoryCueIndex;
        const continueSequence = storySequenceRunning;
        if (segment && cueIndex >= 0) {
          if (continueSequence) {
            void startStorySequence(segment, cueIndex);
          } else {
            void startSingleStoryCue(segment, cueIndex);
          }
        } else {
          cancelStoryPlayback();
          hideSubtitle();
        }
      } else if (lastVoiceDetail) {
        playVoice(lastVoiceDetail);
      }
    });
  });

  motionLoopToggle?.addEventListener('pointerdown', event => event.stopPropagation());
  motionLoopToggle?.addEventListener('pointerup', event => event.stopPropagation());
  motionLoopToggle?.addEventListener('change', event => {
    event.stopPropagation();
    motionLoopEnabled = motionLoopToggle.checked;
    localStorage.setItem('live2d-motion-loop', motionLoopEnabled ? 'yes' : 'no');
    syncMotionLoopToggle();
  });

  exportMotionButton?.addEventListener('pointerdown', event => event.stopPropagation());
  exportMotionButton?.addEventListener('pointerup', event => event.stopPropagation());
  exportMotionButton?.addEventListener('click', event => {
    event.stopPropagation();
    startMotionExport();
  });

  offlineExportButton?.addEventListener('pointerdown', event => event.stopPropagation());
  offlineExportButton?.addEventListener('pointerup', event => event.stopPropagation());
  offlineExportButton?.addEventListener('click', event => {
    event.stopPropagation();
    void startOfflineMotionExport();
  });

  recordButton?.addEventListener('pointerdown', event => event.stopPropagation());
  recordButton?.addEventListener('pointerup', event => event.stopPropagation());
  recordButton?.addEventListener('click', event => {
    event.stopPropagation();

    if (isRecordingActive()) {
      stopRecording();
      return;
    }

    startRecording('manual');
  });

  modelSelect?.addEventListener('pointerdown', event => event.stopPropagation());
  modelSelect?.addEventListener('pointerup', event => event.stopPropagation());
  modelSelect?.addEventListener('change', event => {
    event.stopPropagation();
    const nextGroup = modelPickerGroups.get(modelSelect.value);
    const nextIndex = nextGroup?.items[0]?.index;
    if (typeof nextIndex === 'number') {
      resetPanel();
      stopVoice();
      LAppDelegate.getInstance().setScene(nextIndex);
    }
  });

  storySegmentSelect?.addEventListener('pointerdown', event => event.stopPropagation());
  storySegmentSelect?.addEventListener('pointerup', event => event.stopPropagation());
  storySegmentSelect?.addEventListener('change', event => {
    event.stopPropagation();
    const wasActive = isStoryPlaybackActive();
    cancelStoryPlayback();
    selectedStorySegmentId = storySegmentSelect.value;
    renderStoryCueList(selectedStorySegment());
    syncStoryControls();
    if (wasActive) {
      LAppDelegate.getInstance().returnToIdle();
    }
  });

  storyPlayButton?.addEventListener('pointerdown', event => event.stopPropagation());
  storyPlayButton?.addEventListener('pointerup', event => event.stopPropagation());
  storyPlayButton?.addEventListener('click', event => {
    event.stopPropagation();
    const segment = selectedStorySegment();
    if (segment) {
      void startStorySequence(segment);
    }
  });

  storyStopButton?.addEventListener('pointerdown', event => event.stopPropagation());
  storyStopButton?.addEventListener('pointerup', event => event.stopPropagation());
  storyStopButton?.addEventListener('click', event => {
    event.stopPropagation();
    const wasActive = isStoryPlaybackActive();
    cancelStoryPlayback();
    hideSubtitle();
    currentManualDetail = null;
    lastVoiceDetail = null;
    syncExportMotionButton();
    if (wasActive) {
      LAppDelegate.getInstance().returnToIdle();
    }
  });

  voiceVolumeSlider?.addEventListener('pointerdown', event => event.stopPropagation());
  voiceVolumeSlider?.addEventListener('pointerup', event => event.stopPropagation());
  voiceVolumeSlider?.addEventListener('input', event => {
    event.stopPropagation();
    const nextValue = Number(voiceVolumeSlider.value);
    voiceVolume = Math.max(0, Math.min(1, nextValue / 100));
    localStorage.setItem('live2d-voice-volume', String(Math.round(voiceVolume * 100)));
    syncVoiceVolume();
  });

  viewZoomReset?.addEventListener('pointerdown', event => event.stopPropagation());
  viewZoomReset?.addEventListener('pointerup', event => event.stopPropagation());
  viewZoomReset?.addEventListener('click', event => {
    event.stopPropagation();
    viewZoom = 1.0;
    writeViewZoom(currentModelId, viewZoom);
    syncViewZoom();
  });

  window.addEventListener('live2d-view-scale-applied', event => {
    const detail = (event as CustomEvent<{ scale?: number }>).detail;
    viewZoom = clampViewZoom(Number(detail?.scale));
    writeViewZoom(currentModelId, viewZoom);
    const percent = Math.round(viewZoom * 100);
    if (viewZoomValue) {
      viewZoomValue.textContent = `${percent}%`;
    }
  });

  viewModeButtons.forEach(button => {
    button.addEventListener('pointerdown', event => event.stopPropagation());
    button.addEventListener('pointerup', event => event.stopPropagation());
    button.addEventListener('click', event => {
      event.stopPropagation();
      const nextMode = button.dataset.viewMode;
      if (nextMode !== 'interact' && nextMode !== 'pan') {
        return;
      }
      viewInteractionMode = nextMode;
      localStorage.setItem('live2d-view-interaction-mode', viewInteractionMode);
      syncViewInteractionMode();
    });
  });

  window.addEventListener('keydown', event => {
    if (event.repeat || isEditableTarget(event.target)) {
      return;
    }
    if (event.code === 'Space') {
      event.preventDefault();
      spacePanActive = true;
      dispatchViewInteractionMode();
      return;
    }
    if (event.code === 'KeyH') {
      event.preventDefault();
      togglePanelHidden(!panelHidden);
      return;
    }
    if (event.code === 'KeyF') {
      event.preventDefault();
      toggleFullscreen();
      return;
    }
    if (event.code === 'KeyP') {
      event.preventDefault();
      togglePresentationMode();
    }
  });

  window.addEventListener('keyup', event => {
    if (event.code !== 'Space' || isEditableTarget(event.target)) {
      return;
    }
    event.preventDefault();
    spacePanActive = false;
    dispatchViewInteractionMode();
  });

  window.addEventListener('blur', () => {
    if (!spacePanActive) {
      return;
    }
    spacePanActive = false;
    dispatchViewInteractionMode();
  });

  protagonistToggle?.addEventListener('pointerdown', event => event.stopPropagation());
  protagonistToggle?.addEventListener('pointerup', event => event.stopPropagation());
  protagonistToggle?.addEventListener('change', event => {
    event.stopPropagation();
    hideProtagonist = protagonistToggle.checked;
    writeHideProtagonist(currentModelId, hideProtagonist);
    syncProtagonistToggle();
  });

  subtitleToggle?.addEventListener('pointerdown', event => event.stopPropagation());
  subtitleToggle?.addEventListener('pointerup', event => event.stopPropagation());
  subtitleToggle?.addEventListener('change', event => {
    event.stopPropagation();
    subtitleVisible = subtitleToggle.checked;
    localStorage.setItem('live2d-subtitle-visible', subtitleVisible ? 'yes' : 'no');
    syncSubtitleToggle();
    if (!subtitleVisible) {
      hideSubtitle();
    } else if (activeStoryCue) {
      showSubtitle(selectStoryVoice(activeStoryCue) || { subtitle: activeStoryCue.subtitle }, storyCueMetaLabel(activeStoryCue));
    } else if (lastVoiceDetail) {
      showSubtitle(selectVoice(lastVoiceDetail));
    }
  });

  panelHideButton?.addEventListener('pointerdown', event => event.stopPropagation());
  panelHideButton?.addEventListener('pointerup', event => event.stopPropagation());
  panelHideButton?.addEventListener('click', event => {
    event.stopPropagation();
    panelHidden = true;
    localStorage.setItem('live2d-panel-hidden', 'yes');
    syncPanelHidden();
  });

  panelRestoreButton?.addEventListener('click', event => {
    event.stopPropagation();
    panelHidden = false;
    localStorage.setItem('live2d-panel-hidden', 'no');
    syncPanelHidden();
  });

  freeTouchButton?.addEventListener('pointerdown', event =>
    event.stopPropagation()
  );
  freeTouchButton?.addEventListener('pointerup', event =>
    event.stopPropagation()
  );
  freeTouchButton?.addEventListener('click', event => {
    event.stopPropagation();
    resetPanel();
    stopVoice();
    LAppDelegate.getInstance().returnToIdle();
  });

  window.addEventListener('live2d-model-config-changed', event => {
    renderConfig((event as CustomEvent<ModelConfigEvent>).detail);
  });

  window.addEventListener('live2d-story-touch-action', event => {
    const detail = (event as CustomEvent<StoryTouchAction & { modelId?: string }>).detail;
    if (!detail || (detail.modelId && detail.modelId !== currentModelId)) {
      return;
    }
    void startStoryTouchAction(detail);
  });

  window.addEventListener('live2d-story-tap', event => {
    const detail = (event as CustomEvent<{ modelId?: string }>).detail;
    if (detail?.modelId && detail.modelId !== currentModelId) {
      return;
    }

    const segment = selectedStorySegment();
    if (!segment?.cues.length) {
      return;
    }

    const cueIndex = storyTapNextCueBySegment.get(segment.id) || 0;
    void startSingleStoryCue(segment, cueIndex);
  });

  window.addEventListener('live2d-motion-began', event => {
    const detail = resolveMotionDetail((event as CustomEvent<Partial<MotionDetail>>).detail);
    if (!detail) {
      return;
    }

    if (isStoryPlaybackActive()) {
      currentManualDetail = null;
      highlightMotion(detail);
      startMotionStatus(detail);
      syncExportMotionButton();
      return;
    }

    currentManualDetail = detail;
    highlightMotion(detail);
    startMotionStatus(detail);
    syncExportMotionButton();
  });

  window.addEventListener('live2d-show-action-began', event => {
    const detail = (event as CustomEvent<MotionDetail>).detail;
    if (!detail) {
      return;
    }
    cancelStoryPlayback();

    currentManualDetail = null;
    highlightMotion(detail);
    startMotionStatus(detail);
    syncExportMotionButton();
    playVoice(detail);
  });

  window.addEventListener('live2d-motion-finished', () => {
    if (recordingMode === 'motion') {
      stopRecording();
    }
    if (isStoryPlaybackActive()) {
      return;
    }
    resetPanel();
    stopVoice();
  });
  window.addEventListener('live2d-motion-cancelled', () => {
    if (recordingMode === 'motion') {
      stopRecording();
    }
    if (isStoryPlaybackActive()) {
      return;
    }
    resetPanel();
    stopVoice();
  });

  syncLanguageButtons();
  syncSubtitleToggle();
  syncMotionLoopToggle();
  syncPanelHidden();
  syncVoiceVolume();
  syncViewZoom();
  syncViewInteractionMode();
  syncRecordingIdle();
  syncExportMotionButton();
}


function isOfflineExportMode(): boolean {
  return new URLSearchParams(window.location.search).get('offlineExport') === '1';
}

function isLocalOfflineExportAvailable(): boolean {
  return ['localhost', '127.0.0.1', '::1'].indexOf(window.location.hostname) >= 0;
}

function readLanguage(): VoiceLanguage {
  return localStorage.getItem('live2d-voice-language') === 'en' ? 'en' : 'cn';
}

function readSubtitleVisible(): boolean {
  return localStorage.getItem('live2d-subtitle-visible') !== 'no';
}

function readMotionLoopEnabled(): boolean {
  return localStorage.getItem('live2d-motion-loop') === 'yes';
}

function readPanelHidden(): boolean {
  return localStorage.getItem('live2d-panel-hidden') === 'yes';
}

function readViewInteractionMode(): ViewInteractionMode {
  return localStorage.getItem('live2d-view-interaction-mode') === 'pan' ? 'pan' : 'interact';
}

function readVoiceVolume(): number {
  const rawValue = Number(localStorage.getItem('live2d-voice-volume') || '85');
  if (!Number.isFinite(rawValue)) {
    return 0.85;
  }
  return Math.max(0, Math.min(1, rawValue / 100));
}

function readUnlockedMotions(modelId: string): Set<string> {
  try {
    const rawValue = localStorage.getItem(`${unlockedMotionStorageKeyPrefix}${modelId}`);
    const parsedValue = rawValue ? JSON.parse(rawValue) : [];
    return Array.isArray(parsedValue)
      ? new Set(parsedValue.map(item => String(item)))
      : new Set();
  } catch {
    return new Set();
  }
}

function writeUnlockedMotions(modelId: string, items: Set<string>): void {
  localStorage.setItem(
    `${unlockedMotionStorageKeyPrefix}${modelId}`,
    JSON.stringify([...items])
  );
}
